// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

// Phase 3a (shell conformance) ships the synchronous en-GB bundle only. The
// async multi-locale loader (Cosmos language packs, country flags) is the
// per-page i18n work in Phase 3c (correction C61). The function surface here
// matches the shared Shared-CSS-Reference v3.1 §2.5 contract so UserProfileMenu
// and App can be upgraded to multi-locale without an interface change.

import enGB from './en-GB.json';

export const FALLBACK_LOCALE = 'en-GB';

/** Pre-loaded en-GB messages — the synchronous shell bundle. */
export const fallbackMessages: Record<string, string> = enGB;

/**
 * Returns the display name of a locale code, e.g. "English (United Kingdom)".
 * Uses the browser's Intl.DisplayNames API — renders in the viewer's locale.
 */
export function getLocaleDisplayName(code: string, inLocale?: string): string {
  try {
    const dn = new Intl.DisplayNames([inLocale ?? code], { type: 'language' });
    return dn.of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Language-to-regional-flag overrides for languages with strong regional identity.
 * Uses flag-icons subdivision codes (ISO 3166-2 lowercase with hyphen).
 */
const REGIONAL_FLAG_OVERRIDES: Record<string, string> = {
  'ca': 'es-ct',  // Catalan → Catalonia
  'eu': 'es-pv',  // Basque → Basque Country
  'gl': 'es-ga',  // Galician → Galicia
  'cy': 'gb-wls', // Welsh → Wales
  'gd': 'gb-sct', // Scottish Gaelic → Scotland
};

/**
 * Returns the flag-icons CSS code for a BCP 47 locale code.
 * Checks language-based regional overrides first, then the country segment
 * (e.g. en-GB → gb). Returns empty string for codes that cannot be mapped.
 */
export function getLocaleCountryCode(code: string): string {
  const lang = code.split('-')[0].toLowerCase();
  if (REGIONAL_FLAG_OVERRIDES[lang]) return REGIONAL_FLAG_OVERRIDES[lang];
  const parts = code.split('-');
  const country = parts[parts.length - 1];
  if (!country || country.length !== 2) return '';
  return country.toLowerCase();
}

/**
 * Resolve the initial locale when no explicit preference is saved.
 * Phase 3a installs only en-GB, so the cascade always resolves to the fallback.
 */
export function resolveInitialLocale(): string {
  return FALLBACK_LOCALE;
}

/** Locales with an installed language pack. Phase 3a: en-GB only. */
export async function fetchInstalledLocales(): Promise<string[]> {
  return [FALLBACK_LOCALE];
}
